import { deepMerge, getConfiguration, saveConfiguration } from "../configurationStorage.js"
import { z } from "zod"

/**
 * @typedef {{
 *   loadGraph: {
 *     currentDisplayedStartDate: string | null,
 *     displayLoads: boolean,
 *     displaySpanInSeconds: number,
 *     rowCount: number,
 *     rowsDisplayedStart: number,
 *   },
 *   psiGraph: {
 *     currentDisplayedStartDate: string | null,
 *     displaySpanInSeconds: number,
 *     rowCount: number,
 *     rowsDisplayedStart: number,
 *   },
 *   psiTable: {
 *     aggregateMethod: "divideByMonthlyAverage" | "divideByNextWeekAverage",
 *     currentDisplayedStartDate: string | null,
 *     displaySpanInSeconds: number,
 *     psiSortOrder: "psi" | "spi"
 *     rowCount: number,
 *     rowsDisplayedStart: number,
 *   }
 * }} MPSConfiguration
 */

const VIEW_NAME = "MPS"

/**
 * @type {MPSConfiguration}
 */
const defaultValue = {
  loadGraph: {
    currentDisplayedStartDate: null,
    displayLoads: false,
    displaySpanInSeconds: 60 * 60 * 24 * 14,  // 2 weeks
    rowCount: 5,
    rowsDisplayedStart: 0,
  },
  psiGraph: {
    currentDisplayedStartDate: null,
    displaySpanInSeconds: 60 * 60 * 24 * 14,  // 2 weeks
    psiSortOrder: "spi",
    rowCount: 10,
    rowsDisplayedStart: 0,
  },
  psiTable: {
    aggregateMethod: "divideByNextWeekAverage",
    currentDisplayedStartDate: null,
    displaySpanInSeconds: 60 * 60 * 24 * 14,  // 2 weeks
    psiSortOrder: "spi",
    rowCount: 10,
    rowsDisplayedStart: 0,
  },
}

const MPSConfigurationSchema = z.object({
  loadGraph: z.object({
    currentDisplayedStartDate: z.union([
      z.string(),
      z.null(),
    ]),
    displayLoads: z.boolean(),
    displaySpanInSeconds: z.number(),
    rowCount: z.number(),
    rowsDisplayedStart: z.number(),
  }),
  psiGraph: z.object({
    currentDisplayedStartDate: z.union([
      z.string(),
      z.null(),
    ]),
    displaySpanInSeconds: z.number(),
    rowCount: z.number(),
    rowsDisplayedStart: z.number(),
  }),
  psiTable: z.object({
    aggregateMethod: z.union([
      z.literal("divideByMonthlyAverage"),
      z.literal("divideByNextWeekAverage"),
    ]),
    currentDisplayedStartDate: z.union([
      z.string(),
      z.null(),
    ]),
    displaySpanInSeconds: z.number(),
    rowCount: z.number(),
    rowsDisplayedStart: z.number(),
  }),
})

/**
 * @param {string} userId
 * @param {string} projectId
 * @returns {Promise<MPSConfiguration>}
 */
export async function getMPSConfiguration(userId, projectId) {
  return getConfiguration(VIEW_NAME, userId, projectId, defaultValue)
}

/**
 * @param {any} configuration
 * @param {string} userId
 * @param {string} projectId
 */
export async function saveMPSConfiguration(configuration, userId, projectId) {
  const saved = deepMerge(deepMerge(defaultValue, await getMPSConfiguration(userId, projectId)), configuration)
  try {
    MPSConfigurationSchema.parse(saved)
    saveConfiguration(VIEW_NAME, userId, projectId, saved)
  } catch (e) {
    console.log(e)
    console.error("Malformed configuration for MPS")
  }
}

export function getMPSConfigurationStorage(userId, projectId) {
  return {
    getConfiguration: () => getMPSConfiguration(userId, projectId),
    saveConfiguration: (configuration) => saveMPSConfiguration(configuration, userId, projectId)
  }
}
